import { AuthUser } from '@equestria/spike/lib/controller/auth';
import { createStore, Reducer, Action } from 'redux';
import { EntityRef } from '@equestria/spike';
import _ from 'lodash';
import illumass from './services/illumass';

export interface RarityAppLoginState {
  id: string;
  profileIds: string[];
  email: string;
  name: string;
}

export interface RarityAppTenantState {
  id: string;
  name: string;
}

export interface RarityAppState {
  login?: RarityAppLoginState;
  tenant?: RarityAppTenantState;
  components?: {
    signIn?: {
      rememberMe?: boolean;
      lastEmailTried?: string;
    };
  };
}

const loadState = (): RarityAppState => {
  try {
    const serializedState = localStorage.getItem('state');
    if (serializedState === null) {
      return {};
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error(err);
    return {};
  }
};

const saveState = (state: RarityAppState): void => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('state', serializedState);
  } catch (err) {
    // ignore write errors
    console.error('Failed to save state');
    console.error(err);
  }
};

type RarityAppActionType =
  | 'login'
  | 'logout'
  | 'switchTenant'
  | 'signInOptions';

export interface RarityAppAction extends Action<RarityAppActionType> {
  type: RarityAppActionType;
}

export interface LoginAction extends RarityAppAction {
  type: 'login';
  user: AuthUser;
}

export interface SignInOptionsAction extends RarityAppAction {
  type: 'signInOptions';
  rememberMe: boolean;
  lastEmailTried: string;
}

export interface SwitchTenantAction extends RarityAppAction {
  type: 'switchTenant';
  tenant: EntityRef;
}

const rootReducer: Reducer<RarityAppState, RarityAppAction> = (
  state,
  action
): RarityAppState => {
  switch (action.type) {
    case 'login': {
      const loginAction = action as LoginAction;
      const s = _.set(state ?? {}, ['login'], {
        id: loginAction.user.id ?? '',
        profileIds: loginAction.user.profileIds,
        email: loginAction.user.email ?? '',
        name: (
          (loginAction.user.firstName ?? '') +
          ' ' +
          (loginAction.user.lastName ?? '')
        ).trim(),
      });

      const hasSystemPrivileges = !_.isEmpty(
        _.intersection(
          ['admin', 'auditor', 'support'],
          loginAction.user.profileIds
        )
      );

      const tenantRef: EntityRef | undefined = loginAction.user
        .tenant as EntityRef;

      if (
        (!hasSystemPrivileges || state?.tenant === undefined) &&
        tenantRef !== undefined
      ) {
        _.set(s, ['tenant'], {
          id: tenantRef.id ?? '',
          name: tenantRef.shortName ?? '',
        });
      }

      if (hasSystemPrivileges) {
        const tenantId = _.get(state, ['tenant', 'id'], tenantRef.id);
        _.set(illumass.kuzzle.volatile, ['rumble'], { tenantId: tenantId });
      }

      return s;
    }

    case 'logout': {
      const s = state ?? {};
      _.unset(s, ['login']);
      _.unset(s, ['tenant']);
      _.unset(illumass.kuzzle.volatile, ['rumble']);
      return s;
    }

    case 'switchTenant': {
      const switchTenantAction = action as SwitchTenantAction;
      const tenantId = switchTenantAction.tenant.id;
      const s = state ?? {};
      if (tenantId === undefined) {
        _.unset(illumass.kuzzle.volatile, ['rumble']);
        _.unset(s, ['tenant']);
      } else {
        _.set(illumass.kuzzle.volatile, ['rumble'], { tenantId: tenantId });
        _.set(s, ['tenant'], {
          id: tenantId,
          name: switchTenantAction.tenant.shortName ?? '',
        });
      }
      return s;
    }

    case 'signInOptions': {
      const signInOptions = action as SignInOptionsAction;
      return _.set(state ?? {}, ['components', 'signIn'], {
        rememberMe: signInOptions.rememberMe,
        lastEmailTried: signInOptions.lastEmailTried,
      });
    }

    default:
      return state ?? {};
  }
};

export const store = createStore(rootReducer, loadState());

store.subscribe(
  _.throttle(() => {
    saveState(store.getState());
  }, 1000)
);
